import { useEffect, useState } from "react";
import { SelettoreLingua, useT } from "../lib/i18n.tsx";
import { navigate } from "../lib/router.ts";
import type { Session } from "../lib/types.ts";
import { cx, Menu, VoceMenu } from "./ui.tsx";

/**
 * Scelta del tema: chiaro/scuro e tinta d'accento, nel menu della persona (`MenuUtente`) o, prima dell'accesso, da soli (`SelettoreTema`).
 *
 * I due assi sono attributi su <html> (`data-tema`, `data-accento`) letti dal
 * CSS: cambiarli ridipinge tutto senza che nessun componente sappia di avere un
 * colore. Il valore iniziale è già sul documento — index.html lo applica prima
 * del primo render — quindi qui si legge da lì, non da localStorage.
 */

export const ACCENTI = [
  { id: "acqua", nome: "tema.acqua" },
  { id: "indaco", nome: "tema.indaco" },
  { id: "ametista", nome: "tema.ametista" },
  { id: "ambra", nome: "tema.ambra" },
] as const;

/**
 * Accende la sfumatura fra i due colori per la durata del cambio.
 *
 * Vive fuori da React perché è una proprietà del documento, non di un
 * componente: chi cambia tema e chi cambia tinta accendono la stessa.
 */
const DURATA_TRANSIZIONE = 220;
let spegnimento: ReturnType<typeof setTimeout> | undefined;

function conTransizione() {
  const html = document.documentElement;
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  html.dataset.transizioneTema = "";

  // Due cambi ravvicinati non devono spegnere la transizione del secondo.
  clearTimeout(spegnimento);
  spegnimento = setTimeout(() => {
    delete html.dataset.transizioneTema;
  }, DURATA_TRANSIZIONE);
}

function usaAttributo(nome: "tema" | "accento") {
  const [valore, imposta] = useState(() => document.documentElement.dataset[nome] ?? "");

  useEffect(() => {
    document.documentElement.dataset[nome] = valore;
    // Una preferenza persa (finestra privata, storage pieno) non deve impedire
    // di usare il tema in questa sessione.
    try {
      localStorage.setItem(nome, valore);
    } catch {}
  }, [nome, valore]);

  return [valore, imposta] as const;
}

export function SelettoreTema() {
  const [tema, impostaTema] = usaAttributo("tema");
  const [accento, impostaAccento] = usaAttributo("accento");
  const scuro = tema === "scuro";
  const t = useT();

  return (
    <div className="flex items-center gap-2">
      <SelettoreLingua />
      {/* Le quattro tinte sono una preferenza, non un comando: su schermi
          stretti cedono il posto a ciò che serve per navigare. */}
      <div className="hidden items-center gap-1 sm:flex">
        {ACCENTI.map(({ id, nome }) => (
          <button
            key={id}
            data-accento={id}
            onClick={() => {
              conTransizione();
              impostaAccento(id);
            }}
            title={t.t(nome)}
            aria-label={t.t("tema.colore", { nome: t.t(nome) })}
            aria-pressed={accento === id}
            className={cx(
              "size-4 rounded-full bg-accent-500 transition-transform hover:scale-115",
              accento === id
                ? "ring-2 ring-accent-500/45 ring-offset-2 ring-offset-[var(--superficie-alta)]"
                : "opacity-55 hover:opacity-100",
            )}
          />
        ))}
      </div>

      <button
        onClick={() => {
          conTransizione();
          impostaTema(scuro ? "chiaro" : "scuro");
        }}
        title={t.t(scuro ? "tema.chiaro" : "tema.scuro")}
        aria-label={t.t(scuro ? "tema.chiaro" : "tema.scuro")}
        className="rounded-lg p-1.5 tenue transition-colors hover:bg-[var(--superficie-tenue)] hover:text-[var(--testo)]"
      >
        <svg viewBox="0 0 24 24" className="size-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          {scuro ? (
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
          ) : (
            <>
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}

/** Menu della persona: lingua, tema, tinta d'accento ed Esci. */
export function MenuUtente({
  sessione,
  onEsci,
}: {
  sessione: Session;
  onEsci: () => void;
}) {
  const [tema, impostaTema] = usaAttributo("tema");
  const [accento, impostaAccento] = usaAttributo("accento");
  const scuro = tema === "scuro";
  const t = useT();

  return (
    <Menu
      etichetta={
        <>
          <span className="max-w-[7rem] truncate sm:max-w-[12rem]">{sessione.user.displayName ?? sessione.user.username}</span>
          <span aria-hidden>▾</span>
        </>
      }
    >
      <p className="truncate px-3 py-2 text-sm font-medium">
        {sessione.user.username} · {sessione.user.role}
      </p>
      <VoceMenu onClick={() => navigate("/profilo")}>{t.t("profilo.titolo")}</VoceMenu>
      <div className="border-t px-3 py-2">
        <SelettoreLingua />
      </div>
      <VoceMenu onClick={() => {
        conTransizione();
        impostaTema(scuro ? "chiaro" : "scuro");
      }}>
        {t.t(scuro ? "tema.chiaro" : "tema.scuro")}
      </VoceMenu>
      <div className="flex items-center gap-2 px-3 py-2">
        {ACCENTI.map(({ id, nome }) => (
          <button
            key={id}
            data-accento={id}
            onClick={() => {
              conTransizione();
              impostaAccento(id);
            }}
            title={t.t(nome)}
            aria-label={t.t("tema.colore", { nome: t.t(nome) })}
            aria-pressed={accento === id}
            className={cx(
              "size-5 rounded-full bg-accent-500 transition-transform hover:scale-115",
              accento === id
                ? "ring-2 ring-accent-500/45 ring-offset-2 ring-offset-[var(--superficie-alta)]"
                : "opacity-55 hover:opacity-100",
            )}
          />
        ))}
      </div>
      <div className="border-t pt-1">
        <VoceMenu onClick={onEsci}>{t.t("nav.esci")}</VoceMenu>
      </div>
    </Menu>
  );
}
